import { z } from "zod";
import { GREETING_CARD_RATIO_RECT_EPSILON, greetingCardRectsOverlap } from "./safe-zones";

export const greetingCardOrientationSchema = z.enum(["portrait", "landscape"]);
export const greetingCardFormatSchema = z.enum(["folded", "flat"]);
export const greetingCardLayoutModeSchema = z.enum(["full_width", "cart_drawer"]);
export const greetingCardPaperSizeSchema = z.enum(["A5", "A6", "4x6", "5x7"]);
export const greetingCardFontSourceTypeSchema = z.enum(["builtin", "uploaded"]);
export const greetingCardInsideTextFontModeSchema = z.enum(["brand_only", "shopper_choice"]);
export const greetingCardFrontTextFontPolicySchema = z.enum(["locked", "brand"]);
export const greetingCardFontFormatSchema = z.enum(["woff2", "woff", "opentype", "truetype", ""]);
export const greetingCardCategorySchema = z.string().min(1);

export const greetingCardDesignCategorySchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  sortOrder: z.number().optional(),
});

export const greetingCardRectSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

export const greetingCardPageSideSchema = z.enum(["outside", "inside"]);
export const greetingCardInteractiveElementPlacementSchema = z.enum([
  "none",
  "front",
  "inside",
  "both",
]);

const greetingCardSafeZoneBaseSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().optional(),
    textFlow: z.enum(["blocks-text", "overlay-only"]).optional(),
    bounds: greetingCardRectSchema.refine(
      (bounds) =>
        Number.isFinite(bounds.x) &&
        Number.isFinite(bounds.y) &&
        Number.isFinite(bounds.width) &&
        Number.isFinite(bounds.height) &&
        bounds.x >= 0 &&
        bounds.y >= 0 &&
        bounds.width > 0 &&
        bounds.height > 0 &&
        bounds.x + bounds.width <= 1 &&
        bounds.y + bounds.height <= 1,
      "Safe zone bounds must be finite normalized values within the card side",
    ),
  })
  .strict();

export const greetingCardSafeZoneSchema = z.preprocess((value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const safeZone = value as Record<string, unknown>;
  if (safeZone.textFlow || !safeZone.positioning) return value;

  const { positioning: _positioning, ...rest } = safeZone;
  return {
    ...rest,
    textFlow: "blocks-text",
  };
}, greetingCardSafeZoneBaseSchema);

export const greetingCardDecorationSourceSchema = z.enum(["upload", "emoji", "qr"]);

export const greetingCardDecorationTransformSchema = greetingCardRectSchema
  .extend({
    rotationDeg: z.number(),
    zIndex: z.number(),
  })
  .strict();

export const greetingCardSessionAssetReferenceSchema = z
  .object({
    objectPath: z.string().min(1),
    mimeType: z.string().min(1),
    width: z.number().nullable(),
    height: z.number().nullable(),
    byteSize: z.number().int().nonnegative(),
    url: z.string().url().nullable().optional(),
  })
  .strict();

export const greetingCardDecorationAssetSchema = z
  .object({
    source: greetingCardDecorationSourceSchema.optional(),
    src: z.string().optional(),
    mimeType: z.string().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    emoji: z.string().optional(),
    qrTargetUrl: z.string().optional(),
    qrLabel: z.string().optional(),
    qrColorHex: z.string().optional(),
    storage: greetingCardSessionAssetReferenceSchema.optional(),
  })
  .strict();

export const greetingCardDecorationSchema = z
  .object({
    id: z.string().optional(),
    side: greetingCardPageSideSchema.optional(),
    locked: z.boolean().optional(),
    transform: greetingCardDecorationTransformSchema.optional(),
    asset: greetingCardDecorationAssetSchema,
  })
  .strict();

export const greetingCardDecorationsBySideSchema = z
  .object({
    outside: z.array(greetingCardDecorationSchema).optional(),
    inside: z.array(greetingCardDecorationSchema).optional(),
  })
  .strict();

export const greetingCardStoredAssetSchema = z.object({
  provider: z.literal("supabase"),
  objectPath: z.string(),
  mimeType: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  byteSize: z.number(),
});

export const greetingCardLogoSlotSchema = z.object({
  bounds: greetingCardRectSchema,
  objectFit: z.enum(["contain", "cover"]).optional(),
});

export const greetingCardCssFontWeightSchema = z.union([
  z.enum(["normal", "bold", "lighter", "bolder"]),
  z.enum(["100", "200", "300", "400", "500", "600", "700", "800", "900"]),
]);

export const greetingCardFrontTextLayerSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  type: z.literal("string"),
  required: z.boolean().optional(),
  maxLength: z.number().optional(),
  defaultValue: z.string(),
  bounds: greetingCardRectSchema.optional(),
  defaultFontSize: z.number().optional(),
  fontFamily: z.string().optional(),
  color: z.string().optional(),
  textAlign: z.enum(["left", "center", "right"]).optional(),
  fontWeight: greetingCardCssFontWeightSchema.optional(),
});

export const greetingCardFrontBackgroundSchema = z.union([
  z.object({
    type: z.literal("theme"),
    key: z.enum(["accent", "block"]),
  }),
  z.object({
    type: z.literal("color"),
    color: z.string(),
  }),
]);

export const greetingCardFrontBackdropSchema = z.object({
  source: z.literal("theme"),
  overlayOpacity: z.number().optional(),
});

export const greetingCardFrontConfigSchema = z
  .object({
    textFontPolicy: greetingCardFrontTextFontPolicySchema.optional(),
    type: z.enum(["static", "photo"]),
    preview: z.object({
      frontUrl: z.string(),
      thumbUrl: z.string(),
    }),
    photo: z
      .object({
        slotBounds: greetingCardRectSchema,
        maskUrl: z.string().optional(),
      })
      .optional(),
    textLayers: z.array(greetingCardFrontTextLayerSchema).optional(),
    safeZones: z.array(greetingCardSafeZoneSchema).optional(),
    background: greetingCardFrontBackgroundSchema.optional(),
    backdrop: greetingCardFrontBackdropSchema.optional(),
    logoSlot: greetingCardLogoSlotSchema.optional(),
  })
  .superRefine((front, ctx) => {
    const safeZones = front.safeZones ?? [];
    if (safeZones.length === 0) return;

    if (front.photo?.slotBounds) {
      const overlapsSafeZone = safeZones.some((zone) =>
        greetingCardRectsOverlap(
          front.photo!.slotBounds,
          zone.bounds,
          GREETING_CARD_RATIO_RECT_EPSILON,
        ),
      );
      if (overlapsSafeZone) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["photo", "slotBounds"],
          message: "Photo slot cannot overlap a safe zone",
        });
      }
    }

    front.textLayers?.forEach((layer, index) => {
      if (!layer.bounds) return;
      const overlapsSafeZone = safeZones.some((zone) =>
        greetingCardRectsOverlap(layer.bounds!, zone.bounds, GREETING_CARD_RATIO_RECT_EPSILON),
      );
      if (!overlapsSafeZone) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["textLayers", index, "bounds"],
        message: "Text layer cannot overlap a safe zone",
      });
    });
  });

export const greetingCardInsideConfigSchema = z.object({
  defaultMessage: z.string().optional(),
  suggestions: z.array(z.string()).optional(),
  maxChars: z.number().optional(),
  logoSlot: greetingCardLogoSlotSchema.optional(),
  safeZones: z.array(greetingCardSafeZoneSchema).optional(),
});

export const greetingCardVariantSchema = z.object({
  id: z.string().min(1),
  label: z.string().optional(),
  pages: z.enum(["4pp", "2pp"]).optional(),
  front: greetingCardFrontConfigSchema,
  inside: greetingCardInsideConfigSchema.optional(),
});

export const greetingCardDesignSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  orientation: greetingCardOrientationSchema,
  supportedPaperSizes: z.array(greetingCardPaperSizeSchema),
  categories: z.array(greetingCardCategorySchema),
  tags: z.array(z.string()),
  artist: z.string().optional(),
  cardIdLabel: z.string().optional(),
  printAsset: greetingCardStoredAssetSchema.optional(),
  decorations: greetingCardDecorationsBySideSchema.optional(),
  variants: z.array(greetingCardVariantSchema).min(1),
});

export type GreetingCardOrientation = z.infer<typeof greetingCardOrientationSchema>;
export type GreetingCardFormat = z.infer<typeof greetingCardFormatSchema>;
export type GreetingCardLayoutMode = z.infer<typeof greetingCardLayoutModeSchema>;
export type GreetingCardPaperSize = z.infer<typeof greetingCardPaperSizeSchema>;
export type GreetingCardFontSourceType = z.infer<typeof greetingCardFontSourceTypeSchema>;
export type GreetingCardInsideTextFontMode = z.infer<typeof greetingCardInsideTextFontModeSchema>;
export type GreetingCardFrontTextFontPolicy = z.infer<typeof greetingCardFrontTextFontPolicySchema>;
export type GreetingCardFontFormat = z.infer<typeof greetingCardFontFormatSchema>;
export type GreetingCardCategory = z.infer<typeof greetingCardCategorySchema>;
export type GreetingCardDesignCategory = z.infer<typeof greetingCardDesignCategorySchema>;
export type GreetingCardRect = z.infer<typeof greetingCardRectSchema>;
export type GreetingCardPageSide = z.infer<typeof greetingCardPageSideSchema>;
export type GreetingCardInteractiveElementPlacement = z.infer<
  typeof greetingCardInteractiveElementPlacementSchema
>;
export type GreetingCardSafeZone = z.infer<typeof greetingCardSafeZoneSchema>;
export type GreetingCardDecorationSource = z.infer<typeof greetingCardDecorationSourceSchema>;
export type GreetingCardDecorationTransform = z.infer<typeof greetingCardDecorationTransformSchema>;
export type GreetingCardSessionAssetReference = z.infer<
  typeof greetingCardSessionAssetReferenceSchema
>;
export type GreetingCardDecorationAsset = z.infer<typeof greetingCardDecorationAssetSchema>;
export type GreetingCardDecoration = z.infer<typeof greetingCardDecorationSchema>;
export type GreetingCardDecorationsBySide = z.infer<typeof greetingCardDecorationsBySideSchema>;
export type GreetingCardStoredAsset = z.infer<typeof greetingCardStoredAssetSchema>;
export type GreetingCardLogoSlot = z.infer<typeof greetingCardLogoSlotSchema>;
export type GreetingCardCssFontWeight = z.infer<typeof greetingCardCssFontWeightSchema>;
export type GreetingCardFrontTextLayer = z.infer<typeof greetingCardFrontTextLayerSchema>;
export type GreetingCardFrontBackground = z.infer<typeof greetingCardFrontBackgroundSchema>;
export type GreetingCardFrontBackdrop = z.infer<typeof greetingCardFrontBackdropSchema>;
export type GreetingCardFrontConfig = z.infer<typeof greetingCardFrontConfigSchema>;
export type GreetingCardInsideConfig = z.infer<typeof greetingCardInsideConfigSchema>;
export type GreetingCardVariant = z.infer<typeof greetingCardVariantSchema>;
export type GreetingCardDesign = z.infer<typeof greetingCardDesignSchema>;
